/**
 * FloatingActionBar Component
 * Sticky action bar that appears when locations are selected.
 * Provides bulk actions: Auto-Enrich, Analyze Tags, Publish, Clear.
 */

import { useState } from 'react';
import { Loader2, Sparkles, Tags, Send, X, Ban, ShieldX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCreateBatchOperation } from '@/api/batch-operations';
import { useBatchBlock, useBatchUnblock } from '@/api/block';
import { useToast } from '@/components/ui/use-toast';
import { BlockDialog } from './BlockDialog';
import type { SyncedLocation } from '@/types/api';

interface FloatingActionBarProps {
  /** IDs of selected locations */
  selectedIds: Set<string>;
  /** Full location data for determining button states */
  locations: SyncedLocation[];
  /** Callback to clear selection */
  onClearSelection: () => void;
  /** Callback when an operation is successfully created */
  onOperationCreated?: () => void;
}

/**
 * FloatingActionBar displays bulk action buttons for selected locations.
 *
 * Button enable logic:
 * - Auto-Enrich: Any selected has enrichmentStatus === 'pending'
 * - Analyze Tags: Any selected has enrichmentStatus === 'enriched'
 * - Publish: Any selected has enrichmentStatus === 'enriched' AND publicationStatus !== 'published'
 */
export function FloatingActionBar({
  selectedIds,
  locations,
  onClearSelection,
  onOperationCreated,
}: FloatingActionBarProps): JSX.Element | null {
  const { toast } = useToast();
  const createOperation = useCreateBatchOperation();
  const batchBlock = useBatchBlock();
  const batchUnblock = useBatchUnblock();
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);

  if (selectedIds.size === 0) {
    return null;
  }

  // Get selected locations' data
  const selectedLocations = locations.filter((loc) => selectedIds.has(loc.id));

  // Determine which buttons should be enabled
  const hasAnyPending = selectedLocations.some((loc) => loc.enrichmentStatus === 'pending');
  const hasAnyEnriched = selectedLocations.some((loc) => loc.enrichmentStatus === 'enriched');
  const hasAnyPublishable = selectedLocations.some(
    (loc) => loc.enrichmentStatus === 'enriched' && loc.publicationStatus !== 'published'
  );

  // Block/unblock button states
  const hasAnyPublished = selectedLocations.some((loc) => loc.publicationStatus === 'published');
  const hasAnyBlockable = selectedLocations.some(
    (loc) => !loc.isBlocked && loc.publicationStatus !== 'published'
  );
  const hasAnyBlocked = selectedLocations.some((loc) => loc.isBlocked);

  // Get partner account ID from first selected location (all should be from same account in current UI)
  const partnerAccountId = selectedLocations[0]?.partnerAccountId;

  const handleAutoEnrich = (): void => {
    if (!partnerAccountId) return;

    const locationIds = Array.from(selectedIds);
    createOperation.mutate(
      {
        operationType: 'auto_enrich',
        partnerAccountId,
        locationIds,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Auto-Enrich Started',
            description: `Processing ${String(locationIds.length)} locations`,
          });
          onClearSelection();
          onOperationCreated?.();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to create operation',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleAnalyzeTags = (): void => {
    if (!partnerAccountId) return;

    const locationIds = Array.from(selectedIds);
    createOperation.mutate(
      {
        operationType: 'batch_tags',
        partnerAccountId,
        locationIds,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Tag Analysis Started',
            description: `Analyzing ${String(locationIds.length)} locations`,
          });
          onClearSelection();
          onOperationCreated?.();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to create operation',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handlePublish = (): void => {
    if (!partnerAccountId) return;

    const locationIds = Array.from(selectedIds);
    createOperation.mutate(
      {
        operationType: 'batch_publish',
        partnerAccountId,
        locationIds,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Publish Started',
            description: `Publishing ${String(locationIds.length)} locations`,
          });
          onClearSelection();
          onOperationCreated?.();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to create operation',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleBlock = (reason: string): void => {
    const locationIds = Array.from(selectedIds);
    batchBlock.mutate(
      {
        locationIds,
        reason,
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            const skippedCount = result.skippedCount;
            const blockedCount = result.blockedCount;
            if (skippedCount > 0) {
              toast({
                title: 'Block Completed',
                description: `Blocked ${String(blockedCount)} locations. ${String(skippedCount)} skipped (already blocked or published).`,
              });
            } else {
              toast({
                title: 'Locations Blocked',
                description: `${String(blockedCount)} locations blocked successfully.`,
              });
            }
          }
          setBlockDialogOpen(false);
          onClearSelection();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to block locations',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleUnblock = (): void => {
    const locationIds = Array.from(selectedIds);
    batchUnblock.mutate(
      {
        locationIds,
      },
      {
        onSuccess: (result) => {
          toast({
            title: 'Locations Unblocked',
            description: `${String(result.unblockedCount)} locations unblocked successfully.`,
          });
          onClearSelection();
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to unblock locations',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const isProcessing = createOperation.isPending || batchBlock.isPending || batchUnblock.isPending;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg"
      data-testid="floating-action-bar"
    >
      {/* Selection count */}
      <span
        className="text-sm font-medium text-muted-foreground"
        data-testid="selection-count"
      >
        {String(selectedIds.size)} selected
      </span>

      <div className="h-6 w-px bg-border" />

      {/* Auto-Enrich button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleAutoEnrich}
        disabled={!hasAnyPending || isProcessing}
        data-testid="bulk-auto-enrich-btn"
      >
        {isProcessing ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-1.5 h-4 w-4" />
        )}
        Auto-Enrich
      </Button>

      {/* Analyze Tags button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleAnalyzeTags}
        disabled={!hasAnyEnriched || isProcessing}
        data-testid="bulk-analyze-tags-btn"
      >
        {isProcessing ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
        ) : (
          <Tags className="mr-1.5 h-4 w-4" />
        )}
        Analyze Tags
      </Button>

      {/* Publish button */}
      <Button
        variant="default"
        size="sm"
        onClick={handlePublish}
        disabled={!hasAnyPublishable || isProcessing}
        data-testid="bulk-publish-btn"
      >
        {isProcessing ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-1.5 h-4 w-4" />
        )}
        Publish
      </Button>

      <div className="h-6 w-px bg-border" />

      {/* Block button - disabled if any published locations are selected */}
      {hasAnyPublished ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  data-testid="bulk-block-btn"
                >
                  <Ban className="mr-1.5 h-4 w-4" />
                  Block
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Selected locations include published items. Unpublish them first.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setBlockDialogOpen(true); }}
          disabled={!hasAnyBlockable || isProcessing}
          data-testid="bulk-block-btn"
        >
          {batchBlock.isPending ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Ban className="mr-1.5 h-4 w-4" />
          )}
          Block
        </Button>
      )}

      {/* Unblock button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleUnblock}
        disabled={!hasAnyBlocked || isProcessing}
        data-testid="bulk-unblock-btn"
      >
        {batchUnblock.isPending ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
        ) : (
          <ShieldX className="mr-1.5 h-4 w-4" />
        )}
        Unblock
      </Button>

      <div className="h-6 w-px bg-border" />

      {/* Clear selection button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        disabled={isProcessing}
        data-testid="clear-selection-btn"
      >
        <X className="mr-1.5 h-4 w-4" />
        Clear
      </Button>

      {/* Block Dialog */}
      <BlockDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        onConfirm={handleBlock}
        isLoading={batchBlock.isPending}
        locationCount={selectedIds.size}
      />
    </div>
  );
}
